import { assertUserIsLoggedIn } from "@/services/user";
import { assertCartExists, type CartItemId, hasShippingAddress, ShoppingCart_Schema, type ShoppingCart, CartStatus } from "@/services/marketplace/cart";
import { type MarketplaceItemId } from "@/services/marketplace/item";
import type { AxiosResponse } from "axios";
import { ApiClient } from "@/services/apiClient";
import { useUser } from "@/composables/user";
import { useCart } from "@/composables/marketplace/cart";
import { handleAxiosError } from "@/utilities";
import { type ShippingMethodId } from "@/services/marketplace/shippingMethod";
import { type PaymentMethodId } from "@/services/marketplace/paymentMethod";
import { type AddressId } from "@/services/addresses";

export const fetchFullCart = async (): Promise<ShoppingCart | undefined> => {
  const { user } = useUser();
  assertUserIsLoggedIn(user.value);
  const userId = user.value.id;
  const url = "/api/basket/basket/" + userId;
  return ApiClient.get<ShoppingCart>(url)
    .then(({ data }) => ShoppingCart_Schema.parse(data))
    .catch(handleAxiosError({ 404: () => undefined }));
};

export const fetchCartStatus = async (): Promise<CartStatus> => {
  const url = "/api/basket/basket-status";
  return ApiClient.get(url)
    .then(() => CartStatus.NotEmpty)
    .catch(handleAxiosError({ 404: () => CartStatus.Empty }));
};

export const addItemIntoCart = async (marketplaceItemId: MarketplaceItemId, quantity: number): Promise<ShoppingCart | undefined> => {
  const url = "api/basket/basket-item";
  return ApiClient.post(url, {}, { marketPlaceItemId: marketplaceItemId /* mind different casing */, quantity }).then(updateLocalCartFromApi);
};

export const updateCartItem = async (cartItemId: CartItemId, quantityDiff: number): Promise<ShoppingCart | undefined> => {
  const url = "api/basket/basket-item/" + cartItemId;
  const params = {
    id: cartItemId,
    quantity: quantityDiff,
  };
  return ApiClient.put(url, {}, params).then(updateLocalCartFromApi);
};

export const deleteCartItem = async (cartItemId: CartItemId): Promise<ShoppingCart | undefined> => {
  const url = "api/basket/basket-item/" + cartItemId;
  return ApiClient.delete(url, {}).then(updateLocalCartFromApi);
};

export const saveShippingMethod = async (methodId: ShippingMethodId): Promise<ShoppingCart | undefined> => {
  const url = "api/delivery/basket-delivery";
  return ApiClient.post(url, {}, { delivery: methodId }).then(updateLocalCartFromApi);
};

export const attachShippingAddressToCart = async (cart: ShoppingCart, addressId: AddressId): Promise<ShoppingCart | undefined> => {
  if (hasShippingAddress(cart)) {
    const url = "api/address/basket-address";
    return ApiClient.put(url, {}, { address: addressId }).then(updateLocalCartFromApi);
  } else {
    const url = "api/address/basket-address";
    return ApiClient.post(url, {}, { address: addressId }).then(updateLocalCartFromApi);
  }
};

export const savePaymentMethod = async (methodId: PaymentMethodId): Promise<ShoppingCart | undefined> => {
  const url = "api/payment/basket-payment";
  return ApiClient.post(url, {}, { payment: methodId }).then(updateLocalCartFromApi);
};

export const deleteCart = async (): Promise<AxiosResponse> => {
  const { cart } = useCart();
  assertCartExists(cart.value);
  const url = "api/basket/basket/" + cart.value.id;
  return ApiClient.delete(url).then((data) => {
    cart.value = undefined;
    return data;
  }); // Returns ID of deleted cart
};

// Just a helper DRY
const updateLocalCartFromApi = async (): Promise<ShoppingCart | undefined> => {
  return fetchFullCart().then((updatedCart) => {
    const { cart } = useCart();
    cart.value = updatedCart;
    return updatedCart;
  });
};
